import { ReauthenticationAction } from "./RequestFailure";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useBlocker } from "@tanstack/react-router";
import type { ButtonSize, ButtonVariant } from "@astryxdesign/core/Button";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../lib/styles";
import { errorMessage } from "../lib/api";
import { useDocumentTitle } from "../lib/navigation";
export function PageHeader({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  useDocumentTitle(title);
  return <div {...stylex.props(styles.pageHeading)}>
      <div>
        <h1 {...stylex.props(styles.title)}>{title}</h1>
        <p {...stylex.props(styles.description)}>{description}</p>
      </div>
      {action && <div className="page-action">{action}</div>}
    </div>;
}
export function Stat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: ReactNode;
  detail: string;
  tone?: "success" | "warning";
}) {
  return <div className="stat-card">
      <span className="stat-label">{label}</span>
      <strong className={tone ? `stat-value ${tone}` : "stat-value"}>{value}</strong>
      <span {...stylex.props(styles.small)}>{detail}</span>
    </div>;
}
export function SectionTabs<T extends string>({
  tabs,
  value,
  onChange
}: {
  tabs: {
    id: T;
    label: string;
    count?: number;
  }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return <nav className="section-tabs" aria-label="Page sections">
      {tabs.map(tab => <button key={tab.id} type="button" aria-current={tab.id === value ? "page" : undefined} onClick={() => onChange(tab.id)}>
          {tab.label}
          {tab.count !== undefined && <span>{tab.count}</span>}
        </button>)}
    </nav>;
}
type GuardState = {
  dirty: boolean;
  busy: boolean;
};
type DrawerGuard = {
  register: (id: string, state: GuardState) => void;
  unregister: (id: string) => void;
};
const DrawerGuardContext = createContext<DrawerGuard | null>(null);
const activeForms = new Map<string, GuardState>();
const workListeners = new Set<() => void>();
let discardAllowed = false;
let workSnapshot: GuardState = {
  dirty: false,
  busy: false
};
function notifyWork() {
  const states = [...activeForms.values()];
  const next = {
    dirty: states.some(state => state.dirty),
    busy: states.some(state => state.busy)
  };
  if (next.dirty === workSnapshot.dirty && next.busy === workSnapshot.busy) return;
  workSnapshot = next;
  for (const listener of workListeners) listener();
}
function subscribeWork(callback: () => void) {
  workListeners.add(callback);
  return () => {
    workListeners.delete(callback);
  };
}
function getWorkSnapshot() {
  return workSnapshot;
}
export function useUnsavedWork() {
  return useSyncExternalStore(subscribeWork, getWorkSnapshot, getWorkSnapshot);
}
/** Bypass departure prompts only after an explicit discard decision; restore on failed logout. */
export function allowWorkDiscard() {
  const previous = discardAllowed;
  discardAllowed = true;
  return () => {
    discardAllowed = previous;
  };
}
function DecisionDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
  busy = false,
  cancelWhileBusy = false,
  confirmDisabled = false,
  error
}: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  cancelWhileBusy?: boolean;
  confirmDisabled?: boolean;
  error?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      previous?.focus();
    };
  }, []);
  return <dialog ref={ref} className="decision-dialog" aria-labelledby={titleId} aria-describedby={descriptionId} onCancel={event => {
    event.preventDefault();
    event.stopPropagation();
    if (!busy || cancelWhileBusy) onCancel();
  }}>
      <h2 id={titleId}>{title}</h2>
      <div id={descriptionId} className="decision-description">
        {description}
      </div>
      {error && <p role="alert" {...stylex.props(styles.error)}>
          {error}
        </p>}
      <div className="decision-actions">
        <Button label="Keep working" onClick={onCancel} isDisabled={busy && !cancelWhileBusy} />
        <Button label={confirmLabel} variant="primary" onClick={onConfirm} isDisabled={busy || confirmDisabled} isLoading={busy} />
      </div>
    </dialog>;
}

/** A deliberate, target-named checkpoint for access-removing operations. */
export function ConfirmAction({
  label,
  target,
  description,
  onConfirm,
  busy = false,
  disabled = false,
  size = "sm",
  variant
}: {
  label: string;
  target: string;
  description: string;
  onConfirm: () => void | Promise<void>;
  busy?: boolean;
  disabled?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
}) {
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<ReactNode>("");
  const apply = async () => {
    if (disabled || busy || applying) return;
    setApplying(true);
    setError("");
    try {
      await onConfirm();
      setOpen(false);
    } catch (failure) {
      setError(<>
          {errorMessage(failure)} <ReauthenticationAction error={failure} />
        </>);
    } finally {
      setApplying(false);
    }
  };
  return <>
      <Button label={label} aria-label={`${label} ${target}`} size={size} variant={variant} isDisabled={disabled || busy || applying} isLoading={busy || applying} onClick={() => {
      setError("");
      setOpen(true);
    }} />
      {open && <DecisionDialog title={`${label}?`} description={<>
              <strong className="decision-target">{target}</strong>
              <p>{description}</p>
            </>} confirmLabel={label} onCancel={() => setOpen(false)} onConfirm={() => {
      void apply();
    }} busy={busy || applying} confirmDisabled={disabled} error={error} />}
    </>;
}

/** Registers form state with the drawer, SPA router and native browser unload. */
export function FormGuard({
  dirty,
  busy = false
}: {
  dirty: boolean;
  busy?: boolean;
}) {
  const id = useId();
  const drawer = useContext(DrawerGuardContext);
  const blocker = useBlocker({
    shouldBlockFn: () => !discardAllowed && (dirty || busy),
    disabled: !dirty && !busy,
    enableBeforeUnload: () => !discardAllowed && (dirty || busy),
    withResolver: true
  });
  useEffect(() => {
    activeForms.set(id, {
      dirty,
      busy
    });
    notifyWork();
    drawer?.register(id, {
      dirty,
      busy
    });
  }, [drawer, id, dirty, busy]);
  useEffect(() => () => {
    activeForms.delete(id);
    notifyWork();
    drawer?.unregister(id);
  }, [drawer, id]);
  const {
    status,
    reset,
    proceed
  } = blocker;
  useEffect(() => {
    if (status !== "blocked") return;
    return () => reset?.();
  }, [status, reset]);
  if (status !== "blocked") return null;
  return <DecisionDialog title={busy ? "Save in progress" : "Discard unsaved changes?"} description={busy ? "Wait for the current save to finish before leaving this page." : "Your changes have not been saved. Leaving this page will discard them."} confirmLabel={busy ? "Saving…" : "Discard and leave"} onConfirm={() => {
    if (!busy) proceed?.();
  }} onCancel={() => reset?.()} busy={busy} cancelWhileBusy />;
}
export function Drawer({
  title,
  description,
  children,
  onClose
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [guards, setGuards] = useState<Record<string, GuardState>>({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [busyNotice, setBusyNotice] = useState(false);
  const register = useCallback((id: string, state: GuardState) => {
    setGuards(previous => ({
      ...previous,
      [id]: state
    }));
  }, []);
  const unregister = useCallback((id: string) => {
    setGuards(previous => {
      const next = {
        ...previous
      };
      delete next[id];
      return next;
    });
  }, []);
  const guard = useMemo(() => ({
    register,
    unregister
  }), [register, unregister]);
  const dirty = Object.values(guards).some(state => state.dirty);
  const busy = Object.values(guards).some(state => state.busy);
  const requestClose = () => {
    if (busy) {
      setBusyNotice(true);
      return;
    }
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };
  useEffect(() => {
    const dialog = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    dialog?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  return <dialog ref={ref} className="workspace-drawer" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} onCancel={event => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    requestClose();
  }}>
      <div className="drawer-heading">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description && <p id={descriptionId} {...stylex.props(styles.description)}>
              {description}
            </p>}
        </div>
        <Button label="Close" size="sm" isDisabled={busy} onClick={requestClose} />
      </div>
      <div className="drawer-content">
        <DrawerGuardContext.Provider value={guard}>{children}</DrawerGuardContext.Provider>
        {busyNotice && busy && <ResultStatus>
            Saving your changes. Wait for the operation to finish before closing.
          </ResultStatus>}
      </div>
      {confirmDiscard && <DecisionDialog title="Discard unsaved changes?" description="Your changes have not been saved. Closing this form will discard them." confirmLabel="Discard changes" onConfirm={onClose} onCancel={() => setConfirmDiscard(false)} busy={busy} />}
    </dialog>;
}
export function ResultStatus({
  children
}: {
  children: ReactNode;
}) {
  return <output className="result-status" aria-live="polite" aria-atomic="true">
      {children}
    </output>;
}
export function EmptyState({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return <div className="empty-state">
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {title}. {description}
      </output>
      <span className="empty-symbol" aria-hidden="true">
        <NavIcon name="Servers" />
      </span>
      <h3>{title}</h3>
      <p {...stylex.props(styles.description)}>{description}</p>
      {action}
    </div>;
}
export function SearchField({
  value,
  onChange,
  label = "Search",
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder: string;
}) {
  return <label className="search-field">
      <span className="sr-only">{label}</span>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </svg>
      <input type="search" value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} />
    </label>;
}
/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- Focusable overflow regions provide native keyboard table scrolling. */
export function TableScroll({
  children,
  label = "Data table",
  stickyFirstColumn = false
}: {
  children: ReactNode;
  label?: string;
  stickyFirstColumn?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const region = ref.current;
    if (!region) return;
    const measure = () => setOverflow(region.scrollWidth > region.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    if (region.firstElementChild) observer.observe(region.firstElementChild);
    measure();
    return () => observer.disconnect();
  }, []);
  return <div className="table-scroll-container">
      <p id={hintId} className="table-scroll-hint" hidden={!overflow}>
        Scroll horizontally to view all columns.
      </p>
      <section ref={ref} className="table-scroll-region" data-sticky-first={stickyFirstColumn || undefined} aria-label={label} aria-describedby={overflow ? hintId : undefined} tabIndex={overflow ? 0 : undefined}>
        {children}
      </section>
    </div>;
}
/* oxlint-enable jsx-a11y/no-noninteractive-tabindex */

export function Pagination({
  page,
  total,
  size = 15,
  onChange
}: {
  page: number;
  total: number;
  size?: number;
  onChange: (value: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / size));
  return <div className="table-footer">
      <ResultStatus>
        {total ? `${page * size + 1}–${Math.min((page + 1) * size, total)} of ${total}` : "0 results"}
      </ResultStatus>
      <div className="pagination">
        <Button label="Previous" size="sm" isDisabled={page === 0} onClick={() => onChange(page - 1)} />
        <span>
          Page {page + 1} of {pages}
        </span>
        <Button label="Next" size="sm" isDisabled={page + 1 >= pages} onClick={() => onChange(page + 1)} />
      </div>
    </div>;
}
export function NavIcon({
  name
}: {
  name: string;
}) {
  const paths: Record<string, string> = {
    Requests: "M6 3h12v18H6z M9 8h6 M9 12h6 M9 16h4",
    Sessions: "M3 4h18v13H3z M7 21h10 M12 17v4 M6 8l3 3-3 3 M12 14h4",
    Overview: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
    Servers: "M4 3h16v7H4z M4 14h16v7H4z M7 6h1 M7 17h1",
    Cloud: "M7 18a5 5 0 0 1-1-10 6 6 0 0 1 11-2 6 6 0 1 1 1 12H7",
    Access: "M12 3 3 7v5c0 5 9 9 9 9s9-4 9-9V7z M8 12l3 3 5-6",
    Recordings: "M4 3h16v18H4z M10 8l6 4-6 4z",
    Activity: "M3 12h4l3-8 4 16 3-8h4",
    Connect: "M8 4 2 10l6 6 M16 8l6 6-6 6 M14 3l-4 18",
    ArrowLeft: "M20 12H4m6-6-6 6 6 6",
    ArrowRight: "M4 12h16m-6-6 6 6-6 6"
  };
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] ?? paths.Servers} />
    </svg>;
}